import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import { fetchMediaList } from "../../core/media.core";
import { useTranslation } from "../../core/translation.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import CloseIcon from "../../icons/iconClose.comp";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import MagnifyingIcon from "../../icons/magnifying.icon";
import { Media, MediaFilter } from "../../types/media";
import { inputCss } from "../reuseables/native.comp";
import DirectUploadMediaForm from "./directUploadMedia.comp";
import MediaList from "./mediaList.comp";
import MediaListTiny from "./mediaListTiny.comp";
import MediaListPageNav from "./mediaPageNav.comp";

export enum MediaSelectorMode {
  multiple,
  single,
}

interface MediaSelectorProps {
  id: string;
  mode: MediaSelectorMode;
  defaultSelectedIds?: string[];
  onChanged?: (ids: string[]) => void;
  className: string;
  label: string;
  displayId?: string;
}

export default function MediaSelector(props: MediaSelectorProps) {
  const [ids, setIds] = useState<string[]>([]);
  const [isOpened, setIsOpened] = useState(false);
  const { t, msg } = useTranslation();

  const methods = useForm<MediaFilter>({
    defaultValues: {
      page: 1,
    },
  });

  useEffect(() => {
    setIds(props.defaultSelectedIds === undefined ? [] : props.defaultSelectedIds);
  }, [props.defaultSelectedIds]);

  function toggleSelected(mediaId: string, isSelected: boolean) {
    setIdAndFireChanged((items) => {
      if (isSelected) {
        switch (props.mode) {
          case MediaSelectorMode.multiple:
            return [...items, mediaId];
          case MediaSelectorMode.single:
            return [mediaId];
        }
      } else {
        switch (props.mode) {
          case MediaSelectorMode.multiple:
            return items.filter((id) => mediaId !== id);
          case MediaSelectorMode.single:
            return [];
        }
      }
    });
  }

  function setIdAndFireChanged(newValueCallback: (items: string[]) => string[]) {
    setIds((prevItems) => {
      const newValue = newValueCallback(prevItems);
      if (props.onChanged !== undefined) {
        props.onChanged(newValue);
      }
      return newValue;
    });
  }

  return (
    <div id={props.id} className={props.className}>
      <h2>{props.label}</h2>
      <MediaListFullPane
        ids={ids}
        setIsOpened={setIsOpened}
        isOpened={isOpened}
        methods={methods}
        toggleSelected={toggleSelected}
      />
      <button
        className="mb-4 text-blue-400"
        type="button"
        id="select-media"
        onClick={(e) => {
          e.preventDefault(); // the form will be submitted if we don't do this
          setIsOpened(true);
        }}
      >
        {t(msg.media.addG)}
      </button>

      <DirectUploadMediaForm selectorMode={props.mode} setMediaIds={setIds} toggleSelected={toggleSelected} />
      <MediaListTiny ids={ids} handleDelete={(id: string) => toggleSelected(id, false)} displayId={props.displayId} />
    </div>
  );
}

interface MeidaListFullPanelProps {
  ids: string[];
  setIsOpened: (isOpened: boolean) => void;
  isOpened: boolean;
  methods: UseFormReturn<MediaFilter>;
  toggleSelected: (id: string, isSelected: boolean) => void;
  addSelected?: (ids: string[]) => void;
}

export function MediaListFullPane({ ids, setIsOpened, isOpened, methods, toggleSelected, addSelected }: MeidaListFullPanelProps) {
  const [page, search] = useWatch({ control: methods.control, name: ["page", "search"] });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["mediaList", page, search],
    queryFn: async () => {
      const rst = await fetchMediaList({ pageSize: 36, page, search });
      methods.setValue("total", rst.total);
      return rst.list;
    },
  });

  if (!isOpened) return null;
  if (isError) return <ErrorCenterIcon reason={error.message} />;

  return (
    <FullPanel
      setIsOpened={setIsOpened}
      ids={ids}
      setSelected={toggleSelected}
      mediaList={data!}
      methods={methods}
      listLoading={isLoading}
      addSelected={addSelected}
    />
  );
}

interface FullPanelProps {
  ids: string[];
  mediaList: Media[];
  listLoading: boolean;
  setIsOpened: (isOpened: boolean) => void;
  setSelected: (id: string, isSelected: boolean) => void;
  methods: UseFormReturn<MediaFilter>;
  addSelected?: (ids: string[]) => void;
}

function FullPanel(props: FullPanelProps) {
  const [page, total] = useWatch({ control: props.methods.control, name: ["page", "total"] });
  const { t, msg } = useTranslation();

  return (
    <div id="media-selector-panel" className="fixed inset-0 bg-gray-100 dark:bg-her-backblue w-auto h-auto z-10">
      <button id="close-div" className="text-white bg-her-lightgray2 rounded-full p-1 absolute top-4 right-4">
        <CloseIcon
          className="h-5 w-5"
          onClick={() => {
            props.setIsOpened(false);
          }}
        />
      </button>
      <div className="px-6">
        <div className="pt-8 flex flex-row flex-1 w-full justify-between items-center">
          <MediaListFilterComp methods={props.methods} />
          {props.addSelected && (
            <button
              onClick={() => props.addSelected?.(props.ids)}
              className="self-center py-2 text-white rounded-md px-3 bg-her-dodgerblue"
              type="button"
            >{`${t(msg.media.addSelected)} (${props.ids.length})`}</button>
          )}
        </div>
        <div className="max-h-[80vh] overflow-y-scroll">
          {props.listLoading ? (
            <LoadingOvalCenterIcon />
          ) : (
            <MediaList ids={props.ids} setSelected={props.setSelected} mediaList={props.mediaList} />
          )}
          <MediaListPageNav page={page || 1} size={60} total={total ?? 0} methods={props.methods} />
        </div>
      </div>
    </div>
  );
}

function MediaListFilterComp({ methods }: { methods: UseFormReturn<MediaFilter> }) {
  const { t, msg } = useTranslation();
  const internalMethods = useForm<MediaFilter>({
    defaultValues: {
      search: methods.getValues("search"),
    },
  });

  function onSubmit() {
    methods.setValue("search", internalMethods.getValues("search"));
    methods.setValue("page", 1);
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      onSubmit();
    }
  };

  return (
    <section className="p-6 space-y-2 bg-gray-100 dark:bg-her-backblue flex-1">
      <div>
        <div id="" className="grid grid-cols-[auto_200px] gap-2">
          <div className={`relative grow`}>
            <div className="absolute top-1/2 -translate-y-1/2 left-2">
              <MagnifyingIcon />
            </div>
            <input
              className={`${inputCss} indent-8 w-full my-0 px-4 h-10 outline-none `}
              {...internalMethods.register("search")}
              placeholder={t(msg.general.search)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <input type="button" value={t(msg.general.search)} onClick={onSubmit} />
        </div>
      </div>
    </section>
  );
}
